import _ from 'lodash';
import { PRODUCT_MODELS } from '../util/constants';
import * as reportsService from '../core/api';
import { FEEDBACK_EVENT_ORIGIN, ANALYTICS_EVENT_TYPE } from '../util/constants';
import * as actions from './constants';

const getConfig = () =>
  window.config || {
    DASHBOARDS_ENABLED_110: false,
    DASHBOARD_ENABLED_INSIGHTS_SNAPSHOT_WIDGET: false,
    DASHBOARD_ENABLED_REVISION_PLAN_WIDGET: false,
    DASHBOARDS_ENABLED_REVISION_PLANS_NOT_STARTED: false,
    DASHBOARD_ENABLED_STUDENT_MODULE_REPORT: false,
    DASHBOARDS_SKILLS_COMING_SOON: false,
    DASHBOARDS_MAX_AMOUNT_ASSIGNMENTS: 110,
    DASHBOARDS_MAX_AMOUNT_STUDENTS: 110,
    DASHBOARDS_EXPIRATION_MINUTES: 5,
    DASHBOARDS_QQ_MAX_AMOUNT_CELLS: 1000,
    DASHBOARDS_SKILLS_MAX_AMOUNT_STUDENTS: 20,
    DASHBOARDS_SKILLS_MAX_AMOUNT_ASSIGNMENTS: 50,
  };

const qqAllow = (course, config) =>
  !config.DASHBOARDS_ENABLED_110 &&
  config.DASHBOARDS_MAX_AMOUNT_STUDENTS >= course.enrollments &&
  config.DASHBOARDS_MAX_AMOUNT_ASSIGNMENTS >= course.assignments &&
  config.DASHBOARDS_QQ_MAX_AMOUNT_CELLS >=
    course.enrollments * course.assignments;

const skillsAllow = (course, config) =>
  !config.DASHBOARDS_SKILLS_COMING_SOON &&
  config.DASHBOARDS_SKILLS_MAX_AMOUNT_STUDENTS >= course.enrollments &&
  config.DASHBOARDS_SKILLS_MAX_AMOUNT_ASSIGNMENTS >= course.assignments;

const allow = {
  [PRODUCT_MODELS.qualitative]: qqAllow,
  [PRODUCT_MODELS.quantitative]: qqAllow,
  [PRODUCT_MODELS.goreact]: qqAllow,
  [PRODUCT_MODELS.essentials]: qqAllow,
  [PRODUCT_MODELS.skills]: skillsAllow,
};

const getExpiration = () => {
  const CONFIG = getConfig();
  return new Date(
    Date.now() + parseFloat(CONFIG.DASHBOARDS_EXPIRATION_MINUTES) * 60000
  );
};

export const getOverviewReport = (id) => async (dispatch) => {
  try {
    dispatch({
      type: actions.SET_INDIVIDUAL_REPORT_LOADING,
      payload: { reportKey: actions.REPORT_KEYS.unitOverview, value: true },
    });
    const data = await reportsService.getOverviewReport(id);
    dispatch({
      type: actions.GET_QQ_OVERVIEW_REPORT,
      payload: {
        ...data,
        expireAt: getExpiration(),
      },
    });
  } catch (error) {
    dispatch({
      type: actions.GET_QQ_OVERVIEW_REPORT,
      payload: { error },
    });
  } finally {
    dispatch({
      type: actions.SET_INDIVIDUAL_REPORT_LOADING,
      payload: { reportKey: actions.REPORT_KEYS.unitOverview, value: false },
    });
  }
};

export const removeWidgetFromDashboard = (
  courseId,
  dashboardWidgetId
) => async (dispatch) => {
  dispatch({
    type: actions.DELETE_DASHBOARD_WIDGET,
    payload: { dashboardWidgetId },
  });
  const results = await reportsService.removeWidgetFromDashboard(
    courseId,
    dashboardWidgetId
  );
  return results;
};

export const addWidgetToDashboard = (courseId, dashboardId, widgetId) => async (
  dispatch
) => {
  const dashboard = await reportsService.addWidgetToDashboard(
    courseId,
    dashboardId,
    widgetId
  );
  if (dashboard && !dashboard.error) {
    dispatch({
      type: actions.GET_DASHBOARD,
      payload: dashboard,
    });
  }
  return dashboard;
};

export const getDashboardsWidgets = (courseId) => async (dispatch) => {
  const dashboard = await reportsService.getDashboardsWidgets(courseId);
  dispatch({
    type: actions.GET_DASHBOARD,
    payload: dashboard,
  });
};

const flattenNodes = (nodes, flattened = {}) => {
  for (let i = 0; i < nodes.length; i++) {
    const node = nodes[i];
    flattened[node.id + node.type] = node;
    const children = node.nodes;
    if (children) {
      flattenNodes(children, flattened);
    }
  }

  return flattened;
};

const restoreNodesState = (nodes, nodeMap) => {
  for (let i = 0; i < nodes.length; i++) {
    const node = nodes[i];
    const oldNode = nodeMap[node.id + node.type];
    if (oldNode) {
      node.expanded = oldNode.expanded;
      node.active = oldNode.active;
      if (node.nodes && node.nodes.length > 0) {
        restoreNodesState(node.nodes, nodeMap);
      }
    }
  }
};

const getSkillsNavigationNodes = (skillsNavigationData) => {
  if (!skillsNavigationData) return [];

  const nodes = [];
  const getNode = (node) => ({
    ...node,
    expanded: false,
    active: false,
  });
  let draftNodes = [];
  if (skillsNavigationData) {
    draftNodes = [];
    draftNodes = skillsNavigationData.drafts.map((draft) =>
      getNode({
        id: draft.id,
        type: actions.REPORT_TYPES.reflections,
        title: draft.name,
        url: `reports/type/reflections/${draft.id}`,
      })
    );
  }
  nodes.push(
    getNode({
      id: 'reflections-report',
      type: actions.REPORT_TYPES.reflections,
      title: 'Reflections Snapshot',
      nodes: draftNodes,
    })
  );

  let feedbackNodes = [];
  if (skillsNavigationData && skillsNavigationData.feedback) {
    feedbackNodes = skillsNavigationData.feedback.map((feedback) =>
      getNode({
        id: feedback.id,
        type: actions.REPORT_TYPES.skillsFeedbackSummary,
        title: feedback.name,
        url: `reports/type/skills-feedback-summary/${feedback.id}`,
      })
    );
  }
  nodes.push(
    getNode({
      id: 'feedback-report',
      type: actions.REPORT_TYPES.skillsFeedbackSummary,
      title: 'Are students reviewing my feedback?',
      nodes: feedbackNodes,
    })
  );

  return [
    getNode({
      id: 'overview-report',
      type: 'root',
      title: 'Course Overview',
      nodes,
    }),
  ];
};

export const toggleInsightDetailsModal = (reportType) => (dispatch) => {
  let type =
    reportType === actions.REPORT_TYPE_INSIGHTS_LO
      ? actions.TOGGLE_INSIGHT_LO_DETAILS_MODAL
      : '';
  type =
    reportType === actions.REPORT_TYPE_INSIGHTS_ASSIGNMENT
      ? actions.TOGGLE_INSIGHT_ASSIGNMENT_DETAILS_MODAL
      : type;
  type =
    reportType === actions.REPORT_TYPE_INSIGHTS_EXTERNAL_LO
      ? actions.TOGGLE_INSIGHT_EXTERNAL_LO_MODAL
      : type;

  dispatch({ type });
};

export const setInsightsTab = (insightsTab) => (dispatch) =>
  dispatch({
    type: actions.SET_INSIGHTS_TAB,
    payload: insightsTab,
  });

const updateSkillsNavigationTree = (
  newData,
  prevData,
  existingNavigationBar,
  dispatch
) => {
  const sameStructure = _.isEqual(newData, prevData);
  if (!sameStructure) {
    const nodes = getSkillsNavigationNodes(newData);
    if (existingNavigationBar.length > 0) {
      const nodeMap = flattenNodes(existingNavigationBar);
      restoreNodesState(nodes, nodeMap);
    }
    dispatch({
      type: actions.SET_NAVIGATION_BAR,
      payload: nodes,
    });
  }
};

export const getSkillsNavigationData = (id) => async (dispatch, getState) => {
  const { reports: reportsState } = getState();
  const { skillsNavigation } = reportsState;
  const isValidNav =
    !skillsNavigation.drafts ||
    !skillsNavigation.feedback ||
    skillsNavigation.expireAt < Date.now();
  let data;
  let prevData;
  let payload;

  if (isValidNav) {
    payload = true;
    try {
      dispatch({
        type: actions.SET_SKILLS_NAVIGATION_LOADING,
        payload: true,
      });

      data = await reportsService.getSkillsNavigationData(id);

      if (
        !data ||
        !data.drafts ||
        !data.feedback ||
        (data.drafts.length === 0 && data.feedback.length === 0)
      ) {
        data = { error: true, drafts: [], feedback: [] };
      }

      dispatch({
        type: actions.GET_SKILLS_NAVIGATION,
        payload: {
          expireAt: getExpiration(),
          ...data,
        },
      });

      prevData = reportsState.skills.skillsOverview;
      updateSkillsNavigationTree(
        data,
        prevData,
        reportsState.navigationBar,
        dispatch
      );
    } catch (error) {
      dispatch({
        type: actions.GET_SKILLS_NAVIGATION,
        payload: { error, units: [] },
      });
    } finally {
      dispatch({
        type: actions.SET_SKILLS_NAVIGATION_LOADING,
        payload: false,
      });
    }
  } else {
    payload = false;

    dispatch({
      type: actions.SET_SKILLS_NAVIGATION_LOADING,
      payload,
    });

    data = await reportsService.getSkillsNavigationData(id);
    prevData = prevData || data;

    dispatch({
      type: actions.GET_SKILLS_NAVIGATION,
      payload: {
        expireAt: getExpiration(),
        ...data,
      },
    });

    updateSkillsNavigationTree(
      data,
      prevData,
      reportsState.navigationBar,
      dispatch
    );
  }
};

// Why does this need to be exported? Nothing else calls it
export const getSkillsOverviewReport = (id) => async (dispatch) => {
  try {
    dispatch({
      type: actions.SET_INDIVIDUAL_REPORT_LOADING,
      payload: { reportKey: actions.REPORT_KEYS.skillsOverview, value: true },
    });
    const data = await reportsService.getSkillsOverviewReport(id);
    dispatch({
      type: actions.GET_SKILLS_OVERVIEW_REPORT,
      payload: {
        ...data,
        expireAt: getExpiration(),
      },
    });
  } catch (error) {
    dispatch({
      type: actions.GET_SKILLS_OVERVIEW_REPORT,
      payload: { error },
    });
  } finally {
    dispatch({
      type: actions.SET_INDIVIDUAL_REPORT_LOADING,
      payload: { reportKey: actions.REPORT_KEYS.skillsOverview, value: false },
    });
  }
};

const getInsightWidgets = (id) => async (dispatch) => {
  const CONFIG = getConfig();
  if (CONFIG.DASHBOARD_ENABLED_INSIGHTS_SNAPSHOT_WIDGET) {
    try {
      dispatch({
        type: actions.SET_INDIVIDUAL_REPORT_LOADING,
        payload: { reportKey: actions.REPORT_KEYS.overviewWidget, value: true },
      });
      const data = await reportsService.getInsightWidgets(id);
      dispatch({
        type: actions.GET_WIDGET_REPORT,
        payload: {
          ...data,
          expireAt: getExpiration(),
        },
      });
    } catch (error) {
      dispatch({
        type: actions.GET_WIDGET_REPORT,
        payload: { error },
      });
    } finally {
      dispatch({
        type: actions.SET_INDIVIDUAL_REPORT_LOADING,
        payload: {
          reportKey: actions.REPORT_KEYS.overviewWidget,
          value: false,
        },
      });
    }
  }
};

const getSkillsInsightWidgets = (id) => async (dispatch) => {
  const CONFIG = getConfig();
  if (CONFIG.DASHBOARD_ENABLED_INSIGHTS_SNAPSHOT_WIDGET) {
    try {
      dispatch({
        type: actions.SET_INDIVIDUAL_REPORT_LOADING,
        payload: { reportKey: actions.REPORT_KEYS.overviewWidget, value: true },
      });
      const data = await reportsService.getSkillsOverviewWidget(id);
      dispatch({
        type: actions.GET_WIDGET_REPORT,
        payload: {
          ...data,
          expireAt: getExpiration(),
        },
      });
    } catch (error) {
      dispatch({
        type: actions.GET_WIDGET_REPORT,
        payload: { error },
      });
    } finally {
      dispatch({
        type: actions.SET_INDIVIDUAL_REPORT_LOADING,
        payload: {
          reportKey: actions.REPORT_KEYS.overviewWidget,
          value: false,
        },
      });
    }
  }
};

const getRevisionPlanWidget = (id) => async (dispatch, getState) => {
  const CONFIG = getConfig();
  const { user } = getState();
  if (
    CONFIG.DASHBOARD_ENABLED_INSIGHTS_SNAPSHOT_WIDGET &&
    CONFIG.DASHBOARD_ENABLED_REVISION_PLAN_WIDGET
  ) {
    try {
      dispatch({
        type: actions.SET_INDIVIDUAL_REPORT_LOADING,
        payload: {
          reportKey: actions.REPORT_KEYS.revisionPlanWidget,
          value: true,
        },
      });
      const data = await reportsService.getStudentRevisionPlanOverview(
        id,
        user.user_id
      );
      dispatch({
        type: actions.GET_REVISION_PLAN_WIDGET,
        payload: {
          ...data,
          expireAt: getExpiration(),
        },
      });
    } catch (error) {
      dispatch({
        type: actions.GET_REVISION_PLAN_WIDGET,
        payload: { error },
      });
    } finally {
      dispatch({
        type: actions.SET_INDIVIDUAL_REPORT_LOADING,
        payload: {
          reportKey: actions.REPORT_KEYS.revisionPlanWidget,
          value: false,
        },
      });
    }
  }
};

const hasAuthorization = (user, course, dispatch) => {
  const hasAuth =
    user?.app_metadata?.admin ||
    user?.app_metadata?.support ||
    user?.app_metadata?.producer;
  if (
    !hasAuth &&
    !course.instructor &&
    !(course.productModelId === PRODUCT_MODELS.skills && course.student)
  ) {
    dispatch({
      type: actions.SET_REPORTS_AUTH,
      payload: false,
    });
    dispatch({
      type: actions.SET_REPORTS_LOADING,
      payload: false,
    });
    dispatch({
      type: actions.SET_REPORTS_LOADED,
      payload: true,
    });
    return false;
  }

  return true;
};

const getQQNavigationNodes = (units) => {
  const nodes = [];

  if (units && units.length > 0) {
    const getNode = (node) => ({
      ...node,
      expanded: false,
      active: false,
    });

    const dynamicNodes = [];
    for (let i = 0; i < units.length; i++) {
      const unit = units[i];
      const subunitNodes = [
        getNode({
          id: unit.id,
          type: actions.REPORT_TYPES.unit,
          title: 'Unit Overview',
          url: `reports/type/unit/${unit.id}`,
        }),
      ];

      for (let j = 0; j < unit.subunits.length; j++) {
        const subunit = unit.subunits[j];
        subunitNodes.push(
          getNode({
            id: subunit.id,
            type: actions.REPORT_TYPES.subunit,
            title: subunit.name,
            url: `reports/type/subunit/${subunit.id}`,
          })
        );
      }

      const unitNode = getNode({
        id: unit.id,
        type: 'unit-overview',
        title: unit.name,
        nodes: subunitNodes,
      });
      dynamicNodes.push(unitNode);
    }
    nodes.push(
      getNode({
        id: 'overview-report',
        type: 'root',
        title: 'Course Overview',
        nodes: dynamicNodes,
      })
    );
  }

  return nodes;
};

const updateQQNavigationTree = (
  newUnits,
  prevUnits,
  existingNavigationBar,
  dispatch
) => {
  const sameStructure = _.isEqual(newUnits, prevUnits);
  if (!sameStructure) {
    const nodes = getQQNavigationNodes(newUnits);
    if (existingNavigationBar.length > 0) {
      const nodeMap = flattenNodes(existingNavigationBar);
      restoreNodesState(nodes, nodeMap);
    }
    dispatch({
      type: actions.SET_NAVIGATION_BAR,
      payload: nodes,
    });
  }
};

export const loadUnits = (id) => async (dispatch, getState) => {
  try {
    dispatch({
      type: actions.SET_UNIT_LIST_LOADING,
      payload: true,
    });

    const { reports: reportsState } = getState();
    const { unitList } = reportsState;
    const hasUnits = unitList.units && unitList.units.length > 0;
    if (!hasUnits || unitList.expireAt < Date.now()) {
      let data = await reportsService.getSubunitList(id, true);
      if (!data || !data.units || data.units.length === 0) {
        data = { error: true, units: [] };
      } else {
        data = { error: false, ...data };
      }
      dispatch({
        type: actions.GET_UNIT_LIST,
        payload: {
          expireAt: getExpiration(),
          ...data,
        },
      });

      updateQQNavigationTree(
        data.units,
        unitList,
        reportsState.navigationBar,
        dispatch
      );
    }
  } catch (error) {
    dispatch({
      type: actions.GET_UNIT_LIST,
      payload: { error, units: [] },
    });
  } finally {
    dispatch({
      type: actions.SET_UNIT_LIST_LOADING,
      payload: false,
    });
  }
};

const getUnitReport = (courseId, unitId) => async (dispatch) => {
  try {
    dispatch({
      type: actions.SET_INDIVIDUAL_REPORT_LOADING,
      payload: { reportKey: actions.REPORT_KEYS.unitReport, value: true },
    });

    const data = await reportsService.getUnitReport(courseId, unitId);
    dispatch({
      type: actions.ADD_UNIT_REPORT,
      payload: {
        unitId,
        data: { data },
      },
    });

    dispatch({
      type: actions.GET_UNIT_REPORT,
      payload: {
        ...data,
      },
    });
  } catch (error) {
    dispatch({
      type: actions.GET_UNIT_REPORT,
      payload: { error },
    });
  } finally {
    dispatch({
      type: actions.SET_INDIVIDUAL_REPORT_LOADING,
      payload: { reportKey: actions.REPORT_KEYS.unitReport, value: false },
    });
  }
};

export const updateUnitReport = (unitId) => async (dispatch, getState) => {
  const {
    reports: {
      unitList: { error, units },
      courseAbstract: { id },
    },
  } = getState();

  if (error) {
    dispatch({
      type: actions.GET_UNIT_REPORT,
      payload: {
        error: true,
      },
    });
    return;
  }

  const newactiveUnit = _.find(units, { id: unitId }) || units[0];
  dispatch({
    type: actions.SET_ACTIVE_UNIT,
    payload: newactiveUnit,
  });

  dispatch(getUnitReport(id, newactiveUnit.id));
};

export const loadUnitReport = (id, unitId) => async (dispatch) => {
  await dispatch(loadUnits(id));
  dispatch(updateUnitReport(unitId));
};

const getSubunitReport = (courseId, subunitId) => async (
  dispatch,
  getState
) => {
  try {
    dispatch({
      type: actions.SET_INDIVIDUAL_REPORT_LOADING,
      payload: { reportKey: actions.REPORT_KEYS.subunitReport, value: true },
    });
    const {
      reports: { subunitReportCollection },
    } = getState();
    let data = subunitReportCollection[subunitId];
    if (!data || data.error || data.expireAt < Date.now()) {
      data = await reportsService.getSubunitReport(courseId, subunitId);
      dispatch({
        type: actions.ADD_SUBUNIT_REPORT,
        payload: {
          subunitId,
          data: { ...data, expireAt: getExpiration() },
        },
      });
    }
    dispatch({
      type: actions.GET_SUBUNIT_REPORT,
      payload: {
        ...data,
      },
    });
  } catch (error) {
    dispatch({
      type: actions.GET_SUBUNIT_REPORT,
      payload: { error },
    });
  } finally {
    dispatch({
      type: actions.SET_INDIVIDUAL_REPORT_LOADING,
      payload: { reportKey: actions.REPORT_KEYS.subunitReport, value: false },
    });
  }
};

export const updateSubunitReport = (subunitId) => async (
  dispatch,
  getState
) => {
  const {
    reports: {
      subunitList: { subunits, activeSubunit, error },
      unitList: { units },
      courseAbstract: { id },
    },
  } = getState();

  if (error) {
    dispatch({
      type: actions.GET_SUBUNIT_REPORT,
      payload: {
        error: true,
      },
    });
    return;
  }

  if (activeSubunit && subunitId === activeSubunit.id) return;

  let newActiveSubunit = _.find(subunits, { id: subunitId });

  if (!newActiveSubunit) {
    const payload = {};
    const subunit = units.reduce((subunitItem, unit) => {
      const sub = _.find(unit.subunits, { id: subunitId });
      if (sub) return { ...sub, unit: { ...unit } };

      return subunitItem;
    }, null);

    if (subunit) {
      const { unit, ...sub } = subunit;
      newActiveSubunit = sub;
      payload.unit = { id: unit.id, name: unit.name };
      payload.subunits = unit.subunits;

      dispatch({
        type: actions.GET_SUBUNIT_LIST,
        payload,
      });
    } else {
      newActiveSubunit = subunits[0];
    }
  }

  dispatch({
    type: actions.SET_ACTIVE_SUBUNIT,
    payload: newActiveSubunit,
  });

  dispatch(getSubunitReport(id, newActiveSubunit.id));
};

export const loadSubunitReport = (id, subunitId) => async (
  dispatch,
  getState
) => {
  try {
    dispatch({
      type: actions.SET_SUBUNIT_LIST_LOADING,
      payload: true,
    });

    await dispatch(loadUnits(id));
    const {
      reports: {
        unitList: { units, error },
      },
    } = getState();
    const payload = {};

    if (!error) {
      const unit = _.find(units, { subunits: [{ id: subunitId }] }) || units[0];
      payload.unit = { id: unit.id, name: unit.name };
      payload.subunits = unit.subunits;
    }

    if (error || !payload.subunits || payload.subunits.length === 0) {
      payload.error = true;
      payload.subunits = [];
    }

    dispatch({
      type: actions.GET_SUBUNIT_LIST,
      payload,
    });
    dispatch(updateSubunitReport(subunitId));
  } catch (error) {
    dispatch({
      type: actions.GET_SUBUNIT_LIST,
      payload: { error },
    });
  } finally {
    dispatch({
      type: actions.SET_SUBUNIT_LIST_LOADING,
      payload: false,
    });
  }
};

export const getStudentOverviewReport = (courseId, studentId) => async (
  dispatch,
  getState
) => {
  try {
    const config = getConfig();
    if (config.DASHBOARD_ENABLED_STUDENT_OVERVIEW_REPORT) {
      const {
        reports: { studentReportCollection },
      } = getState();
      dispatch({
        type: actions.SET_INDIVIDUAL_REPORT_LOADING,
        payload: {
          reportKey: actions.REPORT_KEYS.studentOverview,
          value: true,
        },
      });
      let data = studentReportCollection[studentId];
      if (!data || data.error || data.expireAt < Date.now()) {
        data = await reportsService.getStudentOverviewReports(
          courseId,
          studentId
        );
        data.activeUnit = data.activeUnit || null;
        dispatch({
          type: actions.ADD_STUDENT_OVERVIEW_REPORT,
          payload: {
            studentId,
            data: { ...data, expireAt: getExpiration() },
          },
        });
      }
      dispatch({
        type: actions.GET_STUDENT_OVERVIEW_REPORT,
        payload: {
          ...data,
        },
      });
    } else {
      dispatch({
        type: actions.GET_STUDENT_OVERVIEW_REPORT,
        payload: { error: true },
      });
    }
  } catch (error) {
    dispatch({
      type: actions.GET_STUDENT_OVERVIEW_REPORT,
      payload: { error },
    });
  } finally {
    dispatch({
      type: actions.SET_INDIVIDUAL_REPORT_LOADING,
      payload: { reportKey: actions.REPORT_KEYS.studentOverview, value: false },
    });
  }
};

export const getStudentOverviewReportV2 = (courseId, studentId) => async (
  dispatch,
  getState
) => {
  try {
    const config = getConfig();
    if (config.DASHBOARD_ENABLED_STUDENT_OVERVIEW_REPORT) {
      const {
        reports: { studentReportCollection },
      } = getState();
      dispatch({
        type: actions.SET_INDIVIDUAL_REPORT_LOADING,
        payload: {
          reportKey: actions.REPORT_KEYS.studentOverview,
          value: true,
        },
      });
      let data = studentReportCollection[studentId];
      if (!data || data.error || data.expireAt < Date.now()) {
        data = await reportsService.getStudentOverviewReports(
          courseId,
          studentId
        );
        data.activeUnit = data.activeUnit || null;
        dispatch({
          type: actions.ADD_STUDENT_OVERVIEW_REPORT,
          payload: {
            studentId,
            data: { ...data, expireAt: getExpiration() },
          },
        });
      }
      dispatch({
        type: actions.GET_STUDENT_OVERVIEW_REPORT,
        payload: {
          ...data,
        },
      });
    } else {
      dispatch({
        type: actions.GET_STUDENT_OVERVIEW_REPORT,
        payload: { error: true },
      });
    }
  } catch (error) {
    dispatch({
      type: actions.GET_STUDENT_OVERVIEW_REPORT,
      payload: { error },
    });
  } finally {
    dispatch({
      type: actions.SET_INDIVIDUAL_REPORT_LOADING,
      payload: { reportKey: actions.REPORT_KEYS.studentOverview, value: false },
    });
  }
};

const getStudentUnitReport = (courseId, unitId, studentId) => async (
  dispatch,
  getState
) => {
  try {
    const {
      reports: { studentUnitReportCollection },
    } = getState();

    dispatch({
      type: actions.SET_INDIVIDUAL_REPORT_LOADING,
      payload: {
        reportKey: actions.REPORT_KEYS.studentUnitReport,
        value: true,
      },
    });

    const key = `${unitId}--${studentId}`;
    let data = studentUnitReportCollection[key];
    if (!data || data.error || data.expireAt < Date.now()) {
      data = await reportsService.getStudentUnitReport(
        courseId,
        unitId,
        studentId
      );
      dispatch({
        type: actions.ADD_STUDENT_UNIT_REPORT,
        payload: {
          key,
          data: { ...data, expireAt: getExpiration() },
        },
      });
    }
    dispatch({
      type: actions.GET_QQ_STUDENT_UNIT_REPORT,
      payload: {
        ...data,
      },
    });
  } catch (error) {
    dispatch({
      type: actions.GET_QQ_STUDENT_UNIT_REPORT,
      payload: { error },
    });
  } finally {
    dispatch({
      type: actions.SET_INDIVIDUAL_REPORT_LOADING,
      payload: {
        reportKey: actions.REPORT_KEYS.studentUnitReport,
        value: false,
      },
    });
  }
};

export const updateStudentUnitReport = (unitId, studentId) => async (
  dispatch,
  getState
) => {
  const {
    reports: {
      unitList: { units, studentActiveUnit, error },
      studentUnitReport,
      courseAbstract: { id },
    },
  } = getState();

  if (error) {
    dispatch({
      type: actions.GET_QQ_STUDENT_UNIT_REPORT,
      payload: {
        error: true,
      },
    });
    return;
  }

  const sameUnit = studentActiveUnit && unitId === studentActiveUnit.id;
  const sameStudent =
    studentUnitReport &&
    studentUnitReport.student &&
    studentUnitReport.student.id === studentId;

  if (sameUnit && sameStudent) return;

  const newActiveUnit = _.find(units, { id: unitId }) || units[0];
  dispatch({
    type: actions.SET_STUDENT_ACTIVE_UNIT,
    payload: newActiveUnit,
  });

  dispatch(getStudentUnitReport(id, newActiveUnit.id, studentId));
};

export const loadStudentUnitReport = (courseId, unitId, studentId) => async (
  dispatch
) => {
  const CONFIG = getConfig();
  if (CONFIG.DASHBOARD_ENABLED_STUDENT_MODULE_REPORT) {
    await dispatch(loadUnits(courseId));
    dispatch(updateStudentUnitReport(unitId, studentId));
  } else {
    dispatch({
      type: actions.GET_UNIT_LIST,
      payload: { error: true, units: [] },
    });
    dispatch({
      type: actions.SET_UNIT_LIST_LOADING,
      payload: false,
    });
    dispatch({
      type: actions.GET_QQ_STUDENT_UNIT_REPORT,
      payload: { error: true },
    });
    dispatch({
      type: actions.SET_INDIVIDUAL_REPORT_LOADING,
      payload: {
        reportKey: actions.REPORT_KEYS.studentUnitReport,
        value: false,
      },
    });
  }
};

const getStudentSubunitReport = (
  courseId,
  subunitId,
  studentId,
  subunits
) => async (dispatch, getState) => {
  try {
    const {
      reports: { studentSubunitReportCollection },
    } = getState();

    dispatch({
      type: actions.SET_INDIVIDUAL_REPORT_LOADING,
      payload: {
        reportKey: actions.REPORT_KEYS.studentSubunitReport,
        value: true,
      },
    });

    const key = `${subunitId}--${studentId}`;
    let data = studentSubunitReportCollection[key];
    if (!data || data.error || data.expireAt < Date.now()) {
      data = await reportsService.getStudentSubunitReport(
        courseId,
        subunitId,
        studentId
      );
      data.subunits = subunits;
      dispatch({
        type: actions.ADD_STUDENT_SUBUNIT_REPORT,
        payload: {
          key,
          data: { ...data, expireAt: getExpiration() },
        },
      });
    }
    dispatch({
      type: actions.GET_QQ_STUDENT_SUBUNIT_REPORT,
      payload: {
        ...data,
      },
    });
  } catch (error) {
    dispatch({
      type: actions.GET_QQ_STUDENT_SUBUNIT_REPORT,
      payload: { error },
    });
  } finally {
    dispatch({
      type: actions.SET_INDIVIDUAL_REPORT_LOADING,
      payload: {
        reportKey: actions.REPORT_KEYS.studentSubunitReport,
        value: false,
      },
    });
  }
};

export const updateStudentSubunitReport = (subunitId, studentId) => async (
  dispatch,
  getState
) => {
  const {
    reports: {
      unitList: { units, studentActiveSubunit, error },
      studentSubunitReport,
      courseAbstract: { id },
    },
  } = getState();

  if (error) {
    dispatch({
      type: actions.GET_QQ_STUDENT_SUBUNIT_REPORT,
      payload: {
        error: true,
      },
    });
    return;
  }

  const sameSubunit =
    studentActiveSubunit && subunitId === studentActiveSubunit.id;
  const sameStudent =
    studentSubunitReport &&
    studentSubunitReport.student &&
    studentSubunitReport.student.id === studentId;

  if (sameSubunit && sameStudent) return;

  const activeUnit =
    _.find(units, { subunits: [{ id: subunitId }] }) || units[0];
  if (!activeUnit) {
    return;
  }
  const newActiveSubunit =
    _.find(activeUnit.subunits, { id: subunitId }) || activeUnit.subunits[0];
  dispatch({
    type: actions.SET_STUDENT_ACTIVE_SUBUNIT,
    payload: newActiveSubunit,
  });

  dispatch(getStudentSubunitReport(id, newActiveSubunit.id, studentId));
};

export const loadStudentSubunitReport = (
  courseId,
  subunitId,
  studentId
) => async (dispatch) => {
  await dispatch(loadUnits(courseId));
  dispatch(updateStudentSubunitReport(subunitId, studentId));
};

export const getCourseAbstract = (id, reportType) => async (
  dispatch,
  getState
) => {
  try {
    const {
      user,
      reports: { loaded, courseAbstract },
    } = getState();
    const course = await reportsService.getCourseAbstract(id);
    const hasNewAssignments =
      courseAbstract && course.assignments > courseAbstract.assignments;

    if (
      loaded &&
      courseAbstract &&
      (courseAbstract.loading ||
        courseAbstract.id === id ||
        courseAbstract.shortId === id ||
        !hasNewAssignments)
    ) {
      return;
    }

    dispatch({
      type: actions.SET_REPORTS_LOADING,
      payload: true,
    });

    if (!hasAuthorization(user, course, dispatch)) return;

    const CONFIG = getConfig();
    const allowed = allow[course.productModelId]
      ? allow[course.productModelId](course, CONFIG)
      : true;

    dispatch({
      type: actions.SET_REPORTS_COMING_SOON,
      payload: !allowed,
    });

    dispatch({
      type: actions.SET_REPORTS_AUTH,
      payload: true,
    });

    const supportsQQInsights =
      course.productModelId === PRODUCT_MODELS.qualitative ||
      course.productModelId === PRODUCT_MODELS.quantitative ||
      (course.productModelId === PRODUCT_MODELS.essentials &&
        _.get(CONFIG, 'FF_PT_385', false)) ||
      course.productModelId === PRODUCT_MODELS.goreact;
    const isSkills = course.productModelId === PRODUCT_MODELS.skills;

    if (supportsQQInsights) {
      await dispatch(loadUnits(course.id));
    } else if (isSkills && !course.student) {
      await dispatch(getSkillsNavigationData(course.id));
    }

    dispatch({
      type: actions.GET_COURSE_ABSTRACT,
      payload: {
        ...course,
      },
    });
    dispatch({
      type: actions.SET_REPORTS_LOADING,
      payload: false,
    });

    if (!reportType && allowed) {
      if (supportsQQInsights) {
        await dispatch(getInsightWidgets(course.id));
      } else if (isSkills) {
        if (course.student) {
          await dispatch(getRevisionPlanWidget(course.id));
        } else {
          await dispatch(getSkillsInsightWidgets(course.id));
        }
      }
    }

    dispatch({
      type: actions.SET_REPORTS_LOADED,
      payload: true,
    });
  } catch (e) {
    if (e.status === 403) {
      dispatch({
        type: actions.SET_REPORTS_AUTH,
        payload: false,
      });

      dispatch({
        type: actions.SET_REPORTS_LOADING,
        payload: false,
      });

      dispatch({
        type: actions.SET_REPORTS_LOADED,
        payload: true,
      });
    }
  }
};

const reportCalls = {
  [actions.REPORT_KEYS.unitOverview]: getOverviewReport,
  [actions.REPORT_KEYS.overviewWidget]: getInsightWidgets,
  [actions.REPORT_KEYS.skillsOverviewWidget]: getSkillsInsightWidgets,
  [actions.REPORT_KEYS.skillsOverview]: getSkillsOverviewReport,
  [actions.REPORT_KEYS.revisionPlanWidget]: getRevisionPlanWidget,
};

export const externalFrameworkMetadata = (externalFrameworkData) => {
  const isPublished = _.size(externalFrameworkData) > 0;
  const hasResponses = !isPublished
    ? false
    : _.some(
        externalFrameworkData,
        ([, numQuestions, correctOnFirstTry, eventuallyCorrect]) =>
          numQuestions > 0 &&
          (correctOnFirstTry !== '0%' || eventuallyCorrect !== '0%')
      );
  return { isPublished, hasResponses };
};

export const refreshReport = (reportKey) => async (dispatch, getState) => {
  const {
    reports,
    reports: { courseAbstract: course },
    user,
  } = getState();

  if (!hasAuthorization(user, course, dispatch)) return;

  const CONFIG = getConfig();
  const report = reports[reportKey];
  const allowed = allow[course.productModelId]
    ? allow[course.productModelId](course, CONFIG)
    : true;
  const key =
    reportKey === actions.REPORT_KEYS.overviewWidget &&
    course.productModelId === PRODUCT_MODELS.skills
      ? actions.REPORT_KEYS.skillsOverviewWidget
      : reportKey;
  const canFetch = course.student
    ? reportKey === actions.REPORT_KEYS.revisionPlanWidget
    : true;
  if (
    allowed &&
    canFetch &&
    (!report || (!report.loading && report.expireAt < Date.now()))
  ) {
    dispatch({
      type: actions.SET_INDIVIDUAL_REPORT_LOADING,
      payload: { reportKey, value: true },
    });
    await dispatch(reportCalls[key](course.id));
  }
};

export const clearReports = () => (dispatch) => {
  dispatch({ type: actions.CLEAR_REPORTS });
};

export function fetchReflectionSummaryReportData(courseId, courseName) {
  return async (dispatch, getState) => {
    const reports = getState().reports;
    const finalCourseId = courseId || reports.courseAbstract.id;
    const reflectionsSummaryExpiresAt =
      reports.skills.reflectionSummary.expireAt;
    const reflectionsTrendsExpiresAt = reports.skills.reflectionTrends.expireAt;
    const now = new Date();

    if (reflectionsSummaryExpiresAt < now || reflectionsTrendsExpiresAt < now) {
      const reportTitle = `${courseName}: Do students understand the criteria that will be used to provide them feedback?`;
      dispatch({
        type: actions.FETCHING_REFLECTIONS_SUMMARY_TRENDS_DATA,
        payload: {
          reflectionSummary: {
            reportTitle,
          },
        },
      });
      try {
        await dispatch(getSkillsNavigationData(finalCourseId));
        const skillsReflectionData = await reportsService.getReflectionSummaryReport(
          finalCourseId
        );
        if (skillsReflectionData.error) {
          dispatch({
            type: actions.FAILED_FETCH_REFLECTIONS_SUMMARY_TRENDS_DATA,
            payload: {
              error: skillsReflectionData.error,
            },
          });
        } else {
          dispatch({
            type: actions.FETCHED_REFLECTIONS_SUMMARY_TRENDS_DATA,
            payload: {
              reflectionSummary: {
                activeDraft: skillsReflectionData.activeDraft,
                allStudents: skillsReflectionData.draftStudentListPairing,
                dropDownObject: skillsReflectionData.dropDownObject,
                expireAt: getExpiration(),
                reportTitle,
              },
              reflectionTrends: {
                trends: skillsReflectionData.reflectionTrends,
                expireAt: getExpiration(),
              },
              hasPathfinderActivity: skillsReflectionData.hasPathfinderActivity,
              hasWritingActivity: skillsReflectionData.hasWritingActivity,
            },
          });
        }
      } catch (error) {
        dispatch({
          type: actions.FAILED_FETCH_REFLECTIONS_SUMMARY_TRENDS_DATA,
          payload: {
            error,
          },
        });
      }
    }
  };
}

export function updateActiveDraftReflectionSummary(activeDraft, newSelection) {
  return async (dispatch) => {
    const tempActiveDraft = Object.assign({}, activeDraft);
    if (tempActiveDraft !== newSelection) {
      dispatch({
        type: actions.UPDATE_ACTIVE_DRAFT_REFLECTIONS_SUMMARY,
        payload: newSelection,
      });
    }
  };
}

export function fetchNotReviewedSummaryReport(courseId) {
  return async (dispatch, getState) => {
    const expireAt = getState().reports.skills.notReviewedFeedbackSummary
      .expireAt;
    const reportTitle = 'Students who have reviewed 0 feedback';
    const now = new Date();
    if (expireAt < now) {
      try {
        dispatch({
          type: actions.FETCHING_NOT_REVIEWED_FEEDBACK_SUMMARY_REPORT,
          payload: {
            reportTitle,
          },
        });
        const notReviewedFeedbackSummary = await reportsService.getNotReviewedFeedbackSummaryReport(
          courseId
        );
        if (notReviewedFeedbackSummary.error) {
          dispatch({
            type: actions.FAILED_FETCH_NOT_REVIEWED_FEEDBACK_SUMMARY_REPORT,
            payload: {
              error: notReviewedFeedbackSummary.error,
              reportTitle,
            },
          });
        } else {
          dispatch({
            type: actions.FETCHED_NOT_REVIEWED_FEEDBACK_SUMMARY_REPORT,
            payload: {
              notReviewedFeedbackSummaryData:
                notReviewedFeedbackSummary.notReviewedFeedbackSummaryData,
              expireAt: getExpiration(),
              reportTitle,
            },
          });
        }
      } catch (error) {
        dispatch({
          type: actions.FAILED_FETCH_NOT_REVIEWED_FEEDBACK_SUMMARY_REPORT,
          payload: {
            error,
            reportTitle,
          },
        });
      }
    }
  };
}

export function fetchSkillsFeedbackSummaryReport(feedbackId) {
  const reportTitle = 'Are students reviewing my feedback?';
  return async (dispatch, getState) => {
    const reportsState = getState().reports;
    const feedbackSummaryExpiration =
      reportsState.skills.feedbackSummary.expireAt;
    const now = new Date();
    if (
      reportsState.skills.feedbackSummary.activeFeedback !== null &&
      feedbackSummaryExpiration > now
    ) {
      if (
        reportsState.skills.feedbackSummary.activeFeedback.id !== feedbackId
      ) {
        const activeFeedback =
          reportsState.skills.feedbackSummary.feedbackGoals.find(
            (option) => option.id === feedbackId
          ) || reportsState.skills.feedbackSummary.feedbackGoals[0];
        dispatch({
          type: actions.UPDATE_ACTIVE_FEEDBACK_SKILLS_REPORT,
          payload: activeFeedback,
        });
      }
    } else {
      const courseId = reportsState.courseAbstract.id;
      try {
        dispatch({
          type: actions.FETCHING_SKILLS_FEEDBACK_SUMMARY_REPORT,
          payload: {
            reportTitle,
          },
        });
        await dispatch(getSkillsNavigationData(courseId));
        const feedbackSummaryResponse = await reportsService.getFeedbackSummaryReport(
          courseId
        );
        if (feedbackSummaryResponse.error) {
          dispatch({
            type: actions.FAILED_FETCH_SKILLS_FEEDBACK_SUMMARY_REPORT,
            payload: {
              error: feedbackSummaryResponse.error,
              reportTitle,
            },
          });
        } else {
          const { feedbackGoals, feedbackData } = feedbackSummaryResponse;
          const activeFeedback =
            feedbackGoals.find((option) => option.id === feedbackId) ||
            feedbackGoals[0];
          dispatch({
            type: actions.FETCHED_SKILLS_FEEDBACK_SUMMARY_REPORT,
            payload: {
              activeFeedback,
              expireAt: getExpiration(),
              feedbackData,
              feedbackGoals,
              reportTitle,
            },
          });
        }
      } catch (error) {
        dispatch({
          type: actions.FAILED_FETCH_SKILLS_FEEDBACK_SUMMARY_REPORT,
          payload: {
            error,
            reportTitle,
          },
        });
      }
    }
  };
}

export function updateActiveFeedback(newSelection) {
  return async (dispatch, getState) => {
    const {
      activeFeedback,
      feedbackGoals,
    } = getState().reports.skills.feedbackSummary;
    if (activeFeedback !== feedbackGoals[newSelection]) {
      await dispatch(
        fetchSkillsFeedbackSummaryReport(feedbackGoals[newSelection].id)
      );
      const newFeedbackGoals = getState().reports.skills.feedbackSummary
        .feedbackGoals;
      dispatch({
        type: actions.UPDATE_ACTIVE_FEEDBACK_SKILLS_REPORT,
        payload: newFeedbackGoals[newSelection],
      });
    }
  };
}

export const fetchRevisionPlansNotStartedReport = (
  courseId,
  courseName
) => async (dispatch, getState) => {
  const reportTitle = `${courseName}: Overview`;
  try {
    const config = getConfig();
    if (
      config.DASHBOARD_ENABLED_INSIGHTS_SNAPSHOT_WIDGET &&
      config.DASHBOARD_ENABLED_REVISION_PLAN_WIDGET &&
      config.DASHBOARDS_ENABLED_REVISION_PLANS_NOT_STARTED
    ) {
      const {
        reports: {
          skills: { revisionPlansNotStarted },
        },
      } = getState();
      if (revisionPlansNotStarted.expireAt < Date.now()) {
        dispatch({
          type: actions.FETCHING_REVISION_PLANS_NOT_STARTED,
          payload: {
            reportTitle,
          },
        });
        const response = await reportsService.getRevisionPlansNotStartedReport(
          courseId
        );
        if (response.error) {
          dispatch({
            type: actions.FAILED_FETCH_REVISION_PLANS_NOT_STARTED,
            payload: {
              error: response.error,
              reportTitle,
            },
          });
        } else {
          dispatch({
            type: actions.FETCHED_REVISION_PLANS_NOT_STARTED,
            payload: {
              data: response.revisionPlansNotStartedData,
              hasWritingActivity: response.hasWritingActivity,
              hasPathfinderActivity: response.hasPathfinderActivity,
              expireAt: getExpiration(),
              reportTitle,
            },
          });
        }
      }
    } else {
      dispatch({
        type: actions.FAILED_FETCH_REVISION_PLANS_NOT_STARTED,
        payload: {
          error: true,
          reportTitle,
        },
      });
    }
  } catch (error) {
    dispatch({
      type: actions.FAILED_FETCH_REVISION_PLANS_NOT_STARTED,
      payload: {
        error,
        reportTitle,
      },
    });
  }
};

const isReportInNavigationTree = (reportType) =>
  reportType === actions.REPORT_TYPES.unit ||
  reportType === actions.REPORT_TYPES.subunit ||
  reportType === actions.REPORT_TYPES.reflections ||
  reportType === actions.REPORT_TYPES.skillsFeedbackSummary;

export const setCurrentNavigationPath = (reportType, itemId) => async (
  dispatch
) => {
  const nodeToUpdate = isReportInNavigationTree(reportType)
    ? {
        type: reportType,
        id: itemId,
        active: true,
      }
    : null;

  dispatch({
    type: actions.SET_NAVIGATION_NODES_STATE,
    payload: {
      node: nodeToUpdate,
      isActivation: true,
    },
  });
};

export const changeNavigationNodeState = (node) => async (dispatch) => {
  dispatch({
    type: actions.SET_NAVIGATION_NODES_STATE,
    payload: {
      node: { id: node.id, type: node.type },
      isActivation: false,
    },
  });
};

export const clearNavigationNodeState = () => async (dispatch) => {
  dispatch({
    type: actions.SET_NAVIGATION_NODES_STATE,
    payload: {
      node: null,
      isActivation: true,
    },
  });
};

const sendAnalyticsFeedbackEvent = (event) => async (_dispatch, getState) => {
  if (!event) return;

  // Enrich the analytics data with context about user, url, time, etc
  const {
    user = {},
    reports: { courseAbstract = {} },
  } = getState();
  const eventData = {
    ...event,
    origin: FEEDBACK_EVENT_ORIGIN,
    host: window.location.host,
    path: window.location.pathname,
    event_ts: new Date().toISOString(),
    user_id: user?.user_id || '',
    course_id: courseAbstract.id || '',
    course_short_id: courseAbstract.shortId || '',
  };

  await reportsService.sendAnalytics(eventData);
};

/**
 * Send User Analytics events to the analytics pipeline. These should be
 * events generated by implicit user activity. This can include: navigation,
 * sorting, page views, etc
 *
 */
export const sendAnalyticsEvent = (event) => {
  if (!event) return undefined;
  event.eventType = ANALYTICS_EVENT_TYPE;
  return sendAnalyticsFeedbackEvent(event);
};

export const updateSRLWidgetsHeight = (newHeight) => async (dispatch) => {
  dispatch({
    type: actions.UPDATE_SRL_WIDGETS_HEIGHT,
    payload: newHeight,
  });
};
